import { MaterialReactTable, useMaterialReactTable, MRT_ShowHideColumnsButton } from "material-react-table";
import { useEffect, useState } from "react";
import { Box, Checkbox } from "@mui/material";
import "./FEUMRVEnergyMonitoring.scss"
import { useMemo } from "react";
import { convertLocaltoUTCFormat } from "../../../../../../services/DateFormatUtility";
import { callApi } from "../../../../../../services/MonitoringPlanService";
import { AbsDropDown } from "abs-ui-components";
import { MDBCol, MDBRow } from "mdbreact";
import { useSelector } from "react-redux";
import { FEU_CHILD_API_ROUTES } from "../../FEUChildApiRoutes";
import { ENERGYMONITORINGFILTERDROPDOWN } from "../../FEUChildConstant";
import { roundOff } from "../../../../../../services/utilities";

const safeDisplay = (value, decimals = 3) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    if (Number.isNaN(num)) return "";
    return roundOff(num, decimals, decimals);
};

const FEUMRVEnergyMonitoring = () => {
    const [data, setData] = useState([]);
    const [hoveredGroup, setHoveredGroup] = useState(null);
    const [selectedValue, setSelectedValue] = useState("all");
    const [totalRow, setTotalRow] = useState(null);
    const [loading, setLoading] = useState(false);
    const reportDetails = useSelector(
        (state) => state.reports.selectedReportDetails
    );

    // Treat numeric strings like "0", "0.00", "0.000" as zero.
    const isZeroLike = (value) => {
        if (value === null || value === undefined) return false;
        const num = Number(typeof value === "string" ? value.trim() : value);
        return !Number.isNaN(num) && num === 0;
    };

    const transformB5SlippageData = (data) => {
        let transformed =
            data?.flatMap((group) => {
                const sharedProps = {
                    legId: group.legId,
                    groupId: group.groupId || group.legId,
                    isSampleVoyage: group.isSampleVoyage,
                    isVoyage: group.isVoyage,
                    portOfDeparture: group.portOfDeparture,
                    portOfArrival: group.portOfArrival,
                    startTime: group.startTime,
                    endTime: group.endTime,
                    activity: group.activity,
                    diffCriteria: group.diffCriteria,
                    timeAtSea: group.timeAtSea,
                    timeAtSeaThroughIce: group.timeAtSeaThroughIce,
                    timeAtAnchorage: group.timeAtAnchorage,
                    timeAtQuayside: group.timeAtQuayside,
                    cargoCarriedMt: group.cargoCarriedMt,
                    cargoCarriedM3: group.cargoCarriedM3,
                    cargoCarriedDwt: group.cargoCarriedDwt,
                    cargoCarriedPax: group.cargoCarriedPax,
                    totalLegEnergy: group.totalLegEnergy,
                    totalLegEnergyFEUScope: group.totalLegEnergyFEUScope,
                    throughIce: group.throughIce,
                    totalLegDist: group.totalLegDist,
                    totalLegDistThroughIce: group.totalLegDistThroughIce,
                    totalLegDistFEUScope: group.totalLegDistFEUScope,
                    totalLegDistThroughIceFEUScope: group.totalLegDistThroughIceFEUScope,
                    arrivalLatitude: group.arrivalLatitude,
                    arrivalLongitude: group.arrivalLongitude,
                    departureLatitude: group.departureLatitude,
                    departureLongitude: group.departureLongitude,
                };

                if (!group.fuels || group.fuels.length === 0) {
                    return {
                        ...sharedProps,
                        fuelCode: null,
                        fuelType: null,
                        lcvEu: null,
                        consumer: null,
                        cslip: null,
                        cons: null,
                        energy: null,
                        consThroughIce: null,
                        consThroughIceFEUScope: null,
                    };
                }

                return group.fuels.flatMap((fuel) => {
                    if (!fuel.consumers || fuel.consumers.length === 0) {
                        return {
                            ...sharedProps,
                            fuelCode: fuel.fuelCode,
                            fuelType: fuel.fuelType,
                            lcvEu: fuel.lcvEu,
                            consumer: null,
                            cslip: null,
                            cons: null,
                            energy: fuel.energy ?? null,
                            consThroughIce: fuel.consThroughIce ?? null,
                            consThroughIceFEUScope: fuel.consThroughIceFEUScope ?? null,
                        };
                    }

                    return fuel.consumers.map((consumer) => ({
                        ...sharedProps,
                        fuelCode: fuel.fuelCode,
                        fuelType: fuel.fuelType,
                        lcvEu: fuel.lcvEu,
                        consumer: consumer.consumer,
                        cslip: consumer.cslip,
                        cons: consumer.cons,
                        energy: consumer.energy,
                        consThroughIce: consumer.consThroughIce,
                        consThroughIceFEUScope: consumer.consThroughIceFEUScope,
                    }));
                });
            }) || [];

        // sort by legId and then fuelType
        transformed = transformed.sort((a, b) => {
            if (a.legId === b.legId) {
                return (a.fuelType || "").localeCompare(b.fuelType || "");
            }
            return (a.legId || 0) - (b.legId || 0);
        });

        // assign group flags (start, center)
        let grouped = [];
        let startIdx = 0;
        for (let i = 0; i < transformed.length; i++) {
            const current = transformed[i];
            const previous = transformed[i - 1];
            current.isGroupStart = i === 0 || current.legId !== previous?.legId;

            if (current.isGroupStart && i > 0) {
                const groupSize = i - startIdx;
                const centerIdx = startIdx + Math.floor(groupSize / 2);
                grouped[centerIdx].isGroupCenter = true;
                startIdx = i;
            }
            grouped.push(current);
        }
        const lastGroupSize = grouped.length - startIdx;
        const lastCenterIdx = startIdx + Math.floor(lastGroupSize / 2);
        grouped[lastCenterIdx].isGroupCenter = true;

        return grouped;
    };

    const fetchEnergyMonitoring = async (reportMstId, filter = "") => {
        setLoading(true);
        try {
            const response = await callApi({
                method: "GET",
                url: FEU_CHILD_API_ROUTES.GET_ENERGY_MONITORING(reportMstId, filter),
                data: {},
            });
            setLoading(false);
            if (!response?.fuelEuLegs || response.fuelEuLegs.length === 0) {
                setData([]);
                setTotalRow(null);
                return;
            }
            const totalRowData = response.fuelEuLegs.find(r => r.isTotal) || null;
            const transformed = transformB5SlippageData(
                response.fuelEuLegs.filter(r => !r.isTotal)
            );
            setData(transformed);
            setTotalRow(totalRowData);
        } catch (error) {
            setLoading(false);
            console.error("Error fetching energy monitoring data", error);
            setData([]);
            setTotalRow(null);
        }
    };


    useEffect(() => {
        if (reportDetails?.ReportMstId) {
            fetchEnergyMonitoring(reportDetails.ReportMstId);
        }
    }, [reportDetails?.ReportMstId]);

    const cargoColumns = [
        "cargoCarriedMt",
        "cargoCarriedM3",
        "cargoCarriedDwt",
        "cargoCarriedPax",
    ];

    const hasCargoValue = (columnId) =>
        data.some((row) => {
            const value = row[columnId];
            return value !== null && value !== undefined && Number(value) !== 0;
        });

    const columns = useMemo(
        () => {
            const allColumns = [
                {
                    accessorKey: "legId",
                    header: "Leg Id",
                    id: "legId",
                    size: 91,
                    enableHiding: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-end font-weight-bold">
                                {row.original.isGroupCenter ? row.original.legId : ""}
                            </div>
                        )
                    },
                },
                {
                    id: "isSampleVoyage",
                    accessorKey: "isSampleVoyage",
                    header: "Is Sample",
                    size: 95,
                    Cell: ({ row }) => {
                        const isSampleVoyage = row?.original?.isSampleVoyage;
                        return (
                            <div className="d-flex justify-content-center">
                                {row.original.isGroupCenter
                                    ? <Checkbox
                                        className="p-0"
                                        size="small"
                                        disabled={!isSampleVoyage}
                                        checked={isSampleVoyage}
                                    />
                                    : null}

                            </div>
                        );
                    },
                },
                {
                    id: "ports",
                    accessorKey: "ports",
                    header: "Port(s)",
                    size: 300,
                    enableHiding: false,
                    Cell: ({ row }) => {
                        const portOfDeparture = row?.original?.portOfDeparture;
                        const portOfArrival = row?.original?.portOfArrival;
                        const departureLatitude = row?.original?.departureLatitude;
                        const arrivalLatitude = row?.original?.arrivalLatitude;
                        const departureLongitude = row?.original?.departureLongitude;
                        const arrivalLongitude = row?.original?.arrivalLongitude;

                        return (
                            row.original.isGroupCenter ?
                                row?.original?.isVoyage ?
                                    (
                                        <div className="voyage-cell">
                                            <span>{portOfDeparture}</span>
                                            {departureLatitude !== null && departureLongitude !== null && (
                                                <span>
                                                    {" "}(Latitude: {departureLatitude > 0 ? `+${departureLatitude}` : departureLatitude} / Longitude: {departureLongitude > 0 ? `+${departureLongitude}` : departureLongitude})
                                                </span>
                                            )}
                                            {" --> "}
                                            <span>{portOfArrival}</span>
                                            {arrivalLatitude !== null && arrivalLongitude !== null && (
                                                <span>
                                                    {" "}(Latitude: {arrivalLatitude > 0 ? `+${arrivalLatitude}` : arrivalLatitude} / Longitude: {arrivalLongitude > 0 ? `+${arrivalLongitude}` : arrivalLongitude})
                                                </span>
                                            )}
                                        </div>
                                    )
                                    :
                                    (
                                        <div className="voyage-cell">
                                            {portOfArrival} {" --> "} {portOfDeparture}
                                        </div>
                                    )
                                : null
                        );
                    },
                },
                {
                    id: "startTime",
                    accessorKey: "startTime",
                    header: "Start Timestamp",
                    enableHiding: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-center">
                                {row.original.isGroupCenter ? convertLocaltoUTCFormat(row.original.startTime) : ""}
                            </div>
                        )
                    }
                },
                {
                    id: "endTime",
                    accessorKey: "endTime",
                    header: "End Timestamp",
                    enableHiding: false,
                    Cell: ({ row }) => {
                        return (
                            row.original.isGroupCenter ? convertLocaltoUTCFormat(row.original.endTime) : ""
                        )
                    }
                },
                {
                    id: "activity",
                    accessorKey: "activity",
                    header: "Activity",
                    enableHiding: false,
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            row.original.isGroupCenter ? row.original.activity : ""
                        )
                    },
                    size: 100,
                },
                {
                    id: "diffCriteria",
                    accessorKey: "diffCriteria",
                    header: "Differentiating criteria",
                    enableHiding: false,
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            row.original.isGroupCenter ? row.original.diffCriteria : ""
                        )
                    }
                },
                {
                    id: "timeAtSea",
                    accessorKey: "timeAtSea",
                    header: "Time at Sea (h)",
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-end" title={row.original.timeAtSea ?? ""}>
                                {row.original.isGroupCenter ? safeDisplay(row.original.timeAtSea) : ""}
                            </div>
                        )
                    },
                },
                {
                    id: "timeAtSeaThroughIce",
                    accessorKey: "timeAtSeaThroughIce",
                    header: "Time at Sea Through Ice (h)",
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-end" title={row.original.timeAtSeaThroughIce ?? ""}>
                                {row.original.isGroupCenter ? safeDisplay(row.original.timeAtSeaThroughIce) : ""}
                            </div>
                        )
                    },
                },
                {
                    id: "timeAtAnchorage",
                    accessorKey: "timeAtAnchorage",
                    header: "Time at Anchorage (h)",
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-end" title={row.original.timeAtAnchorage ?? ""}>
                                {row.original.isGroupCenter ? safeDisplay(row.original.timeAtAnchorage) : ""}
                            </div>
                        )
                    },
                },
                {
                    id: "timeAtQuayside",
                    accessorKey: "timeAtQuayside",
                    header: "Time at Quayside (h)",
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-end" title={row.original.timeAtQuayside ?? ""}>
                                {row.original.isGroupCenter ? safeDisplay(row.original.timeAtQuayside) : ""}
                            </div>
                        )
                    },
                },
                {
                    id: "cargoCarriedMt",
                    accessorKey: "cargoCarriedMt",
                    header: "Cargo Carried (MT)",
                    enablePinning: false,
                    visibleInShowHideMenu: hasCargoValue("cargoCarriedMt"),
                    Cell: ({ row }) => (
                        <div
                            className="d-flex justify-content-end"
                            title={row.original.cargoCarriedMt ?? ""}
                        >
                            {row.original.isGroupCenter
                                ? safeDisplay(row.original.cargoCarriedMt)
                                : ""}
                        </div>
                    ),
                },
                {
                    id: "cargoCarriedM3",
                    accessorKey: "cargoCarriedM3",
                    header: "Cargo Carried (M3)",
                    visibleInShowHideMenu: hasCargoValue("cargoCarriedM3"),
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-end" title={row.original.cargoCarriedM3 ?? ""}>
                                {row.original.isGroupCenter ? safeDisplay(row.original.cargoCarriedM3) : ""}
                            </div>
                        )
                    },
                },
                {
                    id: "cargoCarriedDwt",
                    accessorKey: "cargoCarriedDwt",
                    header: "Cargo Carried (DWT)",
                    visibleInShowHideMenu: hasCargoValue("cargoCarriedDwt"),
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-end" title={row.original.cargoCarriedDwt ?? ""}>
                                {row.original.isGroupCenter ? safeDisplay(row.original.cargoCarriedDwt) : ""}
                            </div>
                        )
                    },
                },
                {
                    id: "cargoCarriedPax",
                    accessorKey: "cargoCarriedPax",
                    header: "Cargo Carried (PAX)",
                    visibleInShowHideMenu: hasCargoValue("cargoCarriedPax"),
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-end" title={row.original.cargoCarriedPax ?? ""}>
                                {row.original.isGroupCenter ? safeDisplay(row.original.cargoCarriedPax) : ""}
                            </div>
                        )
                    },
                },
                {
                    id: "fuelCode",
                    accessorKey: "fuelCode",
                    header: "Fuel code",
                    enablePinning: false,
                    Cell: ({ row }) => row.original.fuelCode ?? "-",
                },
                {
                    id: "fuelType",
                    accessorKey: "fuelType",
                    header: "Fuel type",
                    enablePinning: false,
                    Cell: ({ row }) => row.original.fuelType ?? "-",
                },
                {
                    id: "lcvEu",
                    accessorKey: "lcvEu",
                    header: "LCV EU (MJ/g)",
                    enablePinning: false,
                    Cell: ({ row }) => {
                        const value = row.original.lcvEu;

                        return (
                            <div className="d-flex justify-content-end" title={value ?? "-"}>
                                {safeDisplay(row.original.lcvEu)}
                            </div>
                        );
                    },
                },
                {
                    id: "consumer",
                    accessorKey: "consumer",
                    header: "Consumer",
                    enablePinning: false,
                    Cell: ({ row }) => row.original.consumer ?? "-",
                },
                {
                    id: "cslip",
                    accessorKey: "cslip",
                    header: "Cslip (%)",
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-end" title={row.original.cslip ?? ""}>
                                {isZeroLike(row.original.cslip) ? "" : (row.original.cslip !== null ? safeDisplay(row.original.cslip) : "-")}
                            </div>
                        )
                    },
                    Footer: () => <b className="footer-total-font d-flex justify-content-end">Total</b>,
                },
                {
                    id: "cons",
                    accessorKey: "cons",
                    header: "CONS (MT)",
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-end" title={row.original.cons ?? ""}>
                                {isZeroLike(row.original.cons) ? "" : (row.original.cons !== null ? safeDisplay(row.original.cons) : "-")}
                            </div>
                        )
                    },
                    Footer: () =>
                        totalRow ? <b className="footer-total-font d-flex justify-content-end" title={totalRow.cons ?? ""}> {safeDisplay(totalRow.cons)}</b> : "",
                },
                {
                    id: "energy",
                    accessorKey: "energy",
                    header: "Energy (GJ)",
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-end" title={row.original.energy ?? ""}>
                                {row.original.energy ? safeDisplay(row.original.energy) : "-"}
                            </div>
                        )
                    },
                    Footer: () =>
                        totalRow ? <b className="footer-total-font d-flex justify-content-end" title={totalRow.energy ?? ""}>{safeDisplay(totalRow.energy)}</b> : "",
                },
                {
                    id: "totalLegEnergy",
                    accessorKey: "totalLegEnergy",
                    header: "Total leg energy (GJ)",
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-end" title={row.original.totalLegEnergy ?? ""}>
                                {row.original.isGroupCenter ? safeDisplay(row.original.totalLegEnergy) : ""}
                            </div>
                        )
                    },
                    Footer: () =>
                        totalRow ? <b className="footer-total-font d-flex justify-content-end" title={totalRow.totalLegEnergy ?? ""}>{safeDisplay(totalRow.totalLegEnergy)}</b> : "",
                },
                {
                    id: "totalLegEnergyFEUScope",
                    accessorKey: "totalLegEnergyFEUScope",
                    header: "Total energy in FEU scope (GJ)",
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-end" title={row.original.totalLegEnergyFEUScope ?? ""}>
                                {row.original.isGroupCenter ? safeDisplay(row.original.totalLegEnergyFEUScope) : ""}
                            </div>
                        )
                    },
                    Footer: () =>
                        totalRow ? (
                            <b className="footer-total-font d-flex justify-content-end" title={totalRow.totalLegEnergyFEUScope ?? ""}>{safeDisplay(totalRow.totalLegEnergyFEUScope)}</b>
                        ) : "",
                },
                {
                    id: "throughIce",
                    accessorKey: "throughIce",
                    header: "Through ice",
                    size: 50,
                    enablePinning: false,
                    Cell: ({ row }) => {
                        const throughIce = row?.original?.throughIce;
                        return (
                            <div className="d-flex justify-content-center">
                                {row.original.isGroupCenter ?
                                    <Checkbox
                                        className="p-0"
                                        size="small"
                                        disabled={!throughIce}
                                        checked={throughIce}
                                    />
                                    : null
                                }

                            </div>
                        );
                    },
                },
                {
                    id: "totalLegDist",
                    accessorKey: "totalLegDist",
                    header: "Total leg DIST (nm)",
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-end" title={row.original.totalLegDist ?? ""}>
                                {row.original.isGroupCenter ? safeDisplay(row.original.totalLegDist) : null}
                            </div>
                        )
                    },
                    Footer: () =>
                        totalRow ? (
                            <b className="footer-total-font d-flex justify-content-end" title={totalRow.totalLegDist ?? ""}> {safeDisplay(totalRow.totalLegDist)}</b>
                        ) : "",
                },
                {
                    id: "totalLegDistThroughIce",
                    accessorKey: "totalLegDistThroughIce",
                    header: "Total leg DIST through ice (nm)",
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-end" title={row.original.totalLegDistThroughIce ?? ""}>
                                {row.original.isGroupCenter ? safeDisplay(row.original.totalLegDistThroughIce) : null}
                            </div>
                        )
                    },
                    Footer: () =>
                        totalRow ? (
                            <b className="footer-total-font d-flex justify-content-end" title={totalRow.totalLegDistThroughIce ?? ""}>{safeDisplay(totalRow.totalLegDistThroughIce)}</b>
                        ) : "",
                },
                {
                    id: "consThroughIce",
                    accessorKey: "consThroughIce",
                    header: "CONS through ice (MT)",
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-end" title={row.original.consThroughIce ?? ""}>
                                {row.original.consThroughIce !== null ? safeDisplay(row.original.consThroughIce) : "-"}
                            </div>
                        )
                    },
                    Footer: () =>
                        totalRow ? (
                            <b className="footer-total-font d-flex justify-content-end" title={totalRow.consThroughIce ?? ""}>{safeDisplay(totalRow.consThroughIce)}</b>
                        ) : "",
                },
                {
                    id: "totalLegDistFEUScope",
                    accessorKey: "totalLegDistFEUScope",
                    header: "Total leg DIST in FEU scope (nm)",
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-end" title={row.original.totalLegDistFEUScope ?? ""}>
                                {row.original.isGroupCenter ? safeDisplay(row.original.totalLegDistFEUScope) : ""}
                            </div>
                        )
                    },
                    Footer: () =>
                        totalRow ? (
                            <b className="footer-total-font d-flex justify-content-end" title={totalRow.totalLegDistFEUScope ?? ""}>{safeDisplay(totalRow.totalLegDistFEUScope)}</b>
                        ) : "",
                },
                {
                    id: "totalLegDistThroughIceFEUScope",
                    accessorKey: "totalLegDistThroughIceFEUScope",
                    header: "Total leg DIST through ice in FEU scope (nm)",
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-end" title={row.original.totalLegDistThroughIceFEUScope ?? ""}>
                                {row.original.isGroupCenter ? safeDisplay(row.original.totalLegDistThroughIceFEUScope) : ""}
                            </div>
                        )
                    },
                    Footer: () =>
                        totalRow ? (
                            <b className="footer-total-font d-flex justify-content-end" title={totalRow.totalLegDistThroughIceFEUScope ?? ""}>{safeDisplay(totalRow.totalLegDistThroughIceFEUScope)}</b>
                        ) : "",
                },
                {
                    id: "consThroughIceFEUScope",
                    accessorKey: "consThroughIceFEUScope",
                    header: "CONS through ice in FEU scope (MT)",
                    enablePinning: false,
                    Cell: ({ row }) => {
                        return (
                            <div className="d-flex justify-content-end" title={row.original.consThroughIceFEUScope ?? ""}>
                                {row.original.consThroughIceFEUScope !== null ? safeDisplay(row.original.consThroughIceFEUScope) : "-"}
                            </div>
                        )
                    },
                    Footer: () =>
                        totalRow ? (
                            <b className="footer-total-font d-flex justify-content-end" title={totalRow.consThroughIceFEUScope ?? ""}>{safeDisplay(totalRow.consThroughIceFEUScope)}</b>
                        ) : "",
                },
            ];

            return allColumns.filter((column) => {
                if (cargoColumns.includes(column.id)) {
                    return hasCargoValue(column.id);
                }

                return true;
            });
        }, [totalRow, data]
    );

    const table = useMaterialReactTable({
        columns: columns,
        data: data || [],
        enableStickyHeader: true,
        enableStickyFooter: true,
        enablePagination: false,
        enableSorting: false,
        enableTableFooter: data?.length > 0,
        enableRowSelection: false,
        enableBottomToolbar: false,
        enableColumnActions: false,
        editDisplayMode: "row",
        enableColumnPinning: true,
        state: {
            isLoading: loading,
        },
        initialState: {
				columnOrder: [
					"legId",
					"isSampleVoyage",
					"ports",
					"startTime",
					"endTime",
					"activity",
					"diffCriteria",
					"timeAtSea",
					"timeAtSeaThroughIce",
					"timeAtAnchorage",
					"timeAtQuayside",
					"cargoCarriedMt",
					"cargoCarriedM3",
					"cargoCarriedDwt",
					"cargoCarriedPax",
					"fuelCode",
					"fuelType",
					"lcvEu",
					"consumer",
					"cslip",
					"cons",
					"energy",
					"totalLegEnergy",
					"totalLegEnergyFEUScope",
					"throughIce",
					"totalLegDist",
					"totalLegDistThroughIce",
					"consThroughIce",
					"totalLegDistFEUScope",
					"totalLegDistThroughIceFEUScope",
					"consThroughIceFEUScope",
				],
			    columnPinning: { left: ["legId","isSampleVoyage","ports","startTime","endTime"] },
			},
        muiTableHeadCellProps: {
            sx: {
                color: "white",
                backgroundColor: "#042a4c",
                borderRight: "1px solid lightgray",
                textAlign: "center",
                alignContent: "center"
            },
        },
        muiTablePaperProps: { sx: { boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)" } },
        displayColumnDefOptions: {
            "mrt-row-actions": {
                header: "Actions",
                size: 90,
                muiTableHeadCellProps: {
                    sx: { borderBottom: "none", borderRight: "1px solid lightgray" },
                },
                muiTableBodyCellProps: {
                    sx: {
                        borderRight: "1px solid lightgray",
                        padding: "4px",
                    },
                },
            },
        },
        muiTableContainerProps: {
            sx: {
                maxHeight: "500px",
                overflowY: "auto",
            },
        },
        muiTableBodyCellProps: {
            sx: {
                borderRight: "1px solid lightgray !important",
                "&:last-child": { borderRight: "none" },
            },
        },
        muiTableBodyRowProps: ({ row, table }) => {
            const rows = table.getRowModel().rows;

            const currentGroup = row.original?.groupId;
            const previousGroup =
                row.index > 0 ? rows[row.index - 1]?.original?.groupId : null;

            const isGroupStart =
                row.index === 0 || currentGroup !== previousGroup;

            const isGroupEnd = row.index === rows.length - 1;

            const groupedColumnIds = [
                "legId",
                "isSampleVoyage",
                "ports",
                "startTime",
                "endTime",
                "activity",
                "diffCriteria",
                "timeAtSea",
                "timeAtSeaThroughIce",
                "timeAtAnchorage",
                "timeAtQuayside",
                "cargoCarriedMt",
                "cargoCarriedM3",
                "cargoCarriedDwt",
                "cargoCarriedPax",
                "totalLegEnergy",
                "totalLegEnergyFEUScope",
                "throughIce",
                "totalLegDist",
                "totalLegDistThroughIce",
                "totalLegDistFEUScope",
                "totalLegDistThroughIceFEUScope",
            ];

            // Get only the currently visible columns, preserving their table order.
            const visibleColumnIds = table
                .getVisibleLeafColumns()
                .map((column) => column.id);

            // Find the positions of the columns that belong to the grouped rows.
            const groupedColumnIndexes = visibleColumnIds
                .map((id, index) =>
                    groupedColumnIds.includes(id) ? index + 1 : null
                )
                .filter(Boolean);

            const selectors = groupedColumnIndexes
                .map((index) => `& td:nth-of-type(${index})`)
                .join(", ");

            return {
                sx: {
                    "& td": {
                        fontSize: "0.875rem",
                    },
                    ...(selectors && {
                        [selectors]: {
                            borderTop: isGroupStart ? "1px solid #ccc" : "none",
                            borderBottom: isGroupEnd ? "1px solid #ccc" : "none",
                        },
                    }),
                },
            };
        },
        muiTableFooterCellProps: {
            sx: {
                backgroundColor: "#f9f9f9",
                borderRight: "1px solid lightgray",
                "&:last-child": { borderRight: "none" },
            },
        },
        renderToolbarInternalActions: ({ table }) => (
            <>
                <MRT_ShowHideColumnsButton
                    id="pc_view_show_hide_Columns"
                    table={table}
                />
            </>
        ),
    });

    return (
        <>
            <div className="mt-2">
                <MDBRow>
                    <MDBCol md="2">
                        <AbsDropDown
                            id="energy-monitoring-dropdown"
                            className="custom-dropdown align-items-center"
                            options={ENERGYMONITORINGFILTERDROPDOWN}
                            selected={selectedValue}
                            handleChange={(selected) => {
                                setSelectedValue(selected.value);
                                fetchEnergyMonitoring(reportDetails.ReportMstId, selected.value)
                            }}
                            placeholder="Filter"
                        />
                    </MDBCol>
                </MDBRow>
            </div>
            <div className="feu-energy-table mt-3">
                <MaterialReactTable table={table} />
            </div>
        </>
    );
};

export default FEUMRVEnergyMonitoring;